import { useEffect } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useNavigate } from 'react-router';

import { getUrl } from 'route/utils/getUrl';
import {
    IRole,
    IStripeSubscriptionStatus,
    CurrentUserDocument,
    OrganisationPlanSubscriptionDocument,
} from 'graphql/types';
import {
    getLoginUrl,
    getTwoFactorSetupUrl,
    getTwoFactorVerifyUrl,
} from 'common/utils/getUrl';
import { SessionManager } from 'user/components/SessionManager';
import { AppPermissionsContext } from 'user/contexts';
import { getParsedAppPermissions } from 'user/utils/permissions';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { EGraphQLError } from 'common/types';

interface IProps {
    children: React.ReactNode;
}

export const IsAuthenticated = ({ children }: IProps) => {
    const navigate = useNavigate();
    const isPlanStatusErrorPage = !!useRouteMatch('PLAN_STATUS_ERROR');
    const isPlanTrialExpiredPage = !!useRouteMatch('PLAN_TRIAL_EXPIRED');

    // We use the current user query to check if a user is logged in.
    const {
        data: currentUserData,
        error,
        loading: loadingUser,
    } = useQuery(CurrentUserDocument);

    const { currentUser } = currentUserData || {};
    const { roles } = currentUser || {};
    const canSeePlanErrorPage =
        roles?.includes(IRole.Owner) || roles?.includes(IRole.Manager);

    const [
        getOrganisationPlanSubscription,
        {
            data: organisationPlanSubscriptionData,
            loading: organisationPlanSubscriptionLoading,
        },
    ] = useLazyQuery(OrganisationPlanSubscriptionDocument);

    let redirectUrl = '';

    if (error && CombinedGraphQLErrors.is(error)) {
        const { errors: graphQLErrors } = error;

        graphQLErrors.some(({ extensions }) => {
            const errorCode = extensions?.code;

            if (errorCode === EGraphQLError.AuthNotLoggedIn) {
                // User is not logged in
                redirectUrl = getLoginUrl();

                return true;
            }

            if (errorCode === EGraphQLError.AuthTwoFactorNotVerified) {
                // User is not verified
                redirectUrl = getTwoFactorVerifyUrl();

                return true;
            }

            if (errorCode === EGraphQLError.AuthTwoFactorNotSetup) {
                // User has not set up two factor authentication
                redirectUrl = getTwoFactorSetupUrl();

                return true;
            }

            return false;
        });
    }

    useEffect(() => {
        if (loadingUser) return;

        if (redirectUrl) {
            window.location.href = redirectUrl;

            return;
        }
    }, [loadingUser, redirectUrl]);

    const { organisation } = organisationPlanSubscriptionData || {};
    const { subscriptionStatus } = organisation || {};

    const subscriptionError =
        subscriptionStatus === IStripeSubscriptionStatus.IncompleteExpired ||
        subscriptionStatus === IStripeSubscriptionStatus.Canceled ||
        subscriptionStatus === IStripeSubscriptionStatus.Incomplete ||
        subscriptionStatus === IStripeSubscriptionStatus.Unpaid ||
        subscriptionStatus === IStripeSubscriptionStatus.PastDue;
    const subscriptionTrialExpired =
        subscriptionStatus === IStripeSubscriptionStatus.TrialExpired;

    useEffect(() => {
        if (!canSeePlanErrorPage) return;

        getOrganisationPlanSubscription();
    }, []);

    useEffect(() => {
        if (isPlanStatusErrorPage || isPlanTrialExpiredPage) {
            if (subscriptionError && isPlanTrialExpiredPage) {
                navigate(getUrl('PLAN_STATUS_ERROR'));

                return;
            }

            if (subscriptionTrialExpired && isPlanStatusErrorPage) {
                navigate(getUrl('PLAN_TRIAL_EXPIRED'));

                return;
            }

            if (
                !organisationPlanSubscriptionLoading &&
                ((isPlanStatusErrorPage && !subscriptionError) ||
                    (isPlanTrialExpiredPage && !subscriptionTrialExpired))
            ) {
                navigate(getUrl('HOME'));
            }

            return;
        }

        if (subscriptionError) {
            navigate(getUrl('PLAN_STATUS_ERROR'));
        }

        if (subscriptionTrialExpired) {
            navigate(getUrl('PLAN_TRIAL_EXPIRED'));
        }
    });

    if (loadingUser || redirectUrl || organisationPlanSubscriptionLoading) {
        return null;
    }

    return (
        <AppPermissionsContext.Provider
            value={getParsedAppPermissions(currentUser?.appPermissions)}
        >
            <SessionManager />
            {children}
        </AppPermissionsContext.Provider>
    );
};
